(() => {
  'use strict';

  const config = window.NycCollectionDiscountBadge || {};
  const cardSelector = 'div.product-grid-item';
  const badgeSelector = [
    '.product-badge__item--saving',
    '.product-badge__item--sale',
    '.sezerium-section__badge--sale',
    '.sezerium-sale-badge'
  ].join(', ');
  const colorNames = ['color', 'colour', 'χρώμα', 'χρωμα'];
  const cardProductCache = new WeakMap();
  const productRequests = new Map();

  const number = (value) => Number(value || 0);
  const schedule = (callback) => queueMicrotask(callback);

  function collectionData() {
    if (typeof SezeriumCollection !== 'undefined') return SezeriumCollection;
    return window.SezeriumCollection || null;
  }

  function normalize(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }

  function productHandle(card) {
    const href = card.querySelector('a[href*="/products/"]')?.href;
    const handle = href?.match(/\/products\/([^/?#]+)/)?.[1];
    return handle ? decodeURIComponent(handle) : null;
  }

  function normalizeProduct(data) {
    if (!data || !Array.isArray(data.variants)) return null;

    return {
      ...data,
      options: (data.options || []).map((option) => typeof option === 'string' ? option : option.name),
      variants: data.variants.map((variant) => ({
        ...variant,
        compareAtPrice: variant.compareAtPrice ?? variant.compare_at_price
      }))
    };
  }

  function cardProduct(card, handle) {
    if (cardProductCache.has(card)) return cardProductCache.get(card);

    const payload = card.dataset.nycDiscountProduct;
    if (!payload) return null;

    try {
      const product = normalizeProduct(JSON.parse(payload));
      if (product?.handle === handle) {
        cardProductCache.set(card, product);
        return product;
      }
    } catch (_) {
      // Keep collection cards functional when a malformed payload is encountered.
    }

    return null;
  }

  function requestProduct(handle) {
    if (!productRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      const request = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => data?.handle === handle ? normalizeProduct(data) : null)
        .catch(() => null);
      productRequests.set(handle, request);
    }
    return productRequests.get(handle);
  }

  function productFromCard(card) {
    const handle = productHandle(card);
    if (!handle) return null;

    return cardProduct(card, handle)
      || collectionData()?.products?.find((product) => product.handle === handle)
      || null;
  }

  function selectedVariantFromCard(card, product) {
    const href = card.querySelector('a[href*="variant="]')?.href;
    const id = new URL(href || window.location.href).searchParams.get('variant');
    return product?.variants?.find((variant) => String(variant.id) === id) || null;
  }

  function colorPosition(product) {
    const options = product?.options || [];
    const index = options.findIndex((option) => colorNames.includes(normalize(option)));
    return index === -1 ? null : index + 1;
  }

  function inScopeVariants(product, selectedVariant) {
    const position = colorPosition(product);
    if (!selectedVariant || !position) return product?.variants || [];

    const color = selectedVariant[`option${position}`];
    return product.variants.filter((variant) => variant[`option${position}`] === color);
  }

  function badgeText(product, selectedVariant) {
    const percentages = inScopeVariants(product, selectedVariant)
      .filter((variant) => number(variant.compareAtPrice) > number(variant.price))
      .map((variant) => Math.round((number(variant.compareAtPrice) - number(variant.price)) * 100 / number(variant.compareAtPrice)));

    if (!percentages.length) return null;

    const minimum = Math.min(...percentages);
    const maximum = Math.max(...percentages);
    if (config.display === 'highest' || minimum === maximum) return `${maximum}% Off`;
    return `${minimum}-${maximum}% Off`;
  }

  function savingBadgeContainer(card) {
    let container = card.querySelector('.product-badge');
    if (container) return container;

    container = document.createElement('div');
    container.className = 'product-badge';
    (card.querySelector('.product-grid-item__image') || card).appendChild(container);
    return container;
  }

  function normalizeSavingBadge(card, text) {
    const genericBadges = [...card.querySelectorAll('.product-badge__item--sale, .sezerium-section__badge--sale, .sezerium-sale-badge')];
    const savingBadges = [...card.querySelectorAll('.product-badge__item--saving')];

    if (!text) {
      genericBadges.concat(savingBadges).forEach((badge) => badge.remove());
      return;
    }

    genericBadges.forEach((badge) => badge.remove());

    const badge = savingBadges[0] || document.createElement('div');
    if (!badge.parentElement) savingBadgeContainer(card).appendChild(badge);
    if (badge.className !== 'product-badge__item product-badge__item--saving') {
      badge.className = 'product-badge__item product-badge__item--saving';
    }

    const hasStandardLabel = badge.children.length === 1 && badge.firstElementChild?.tagName === 'SPAN';
    if (hasStandardLabel) {
      if (badge.firstElementChild.textContent !== text) badge.firstElementChild.textContent = text;
    } else {
      const label = document.createElement('span');
      label.textContent = text;
      badge.replaceChildren(label);
    }

    savingBadges.slice(1).forEach((duplicate) => duplicate.remove());
  }

  function updateCard(card, product, selectedVariant) {
    if (!card) return;

    const resolvedProduct = normalizeProduct(product) || productFromCard(card);
    if (resolvedProduct) {
      normalizeSavingBadge(
        card,
        badgeText(resolvedProduct, selectedVariant || selectedVariantFromCard(card, resolvedProduct))
      );
      return;
    }

    const handle = productHandle(card);
    if (!handle) return;
    requestProduct(handle).then((requestedProduct) => {
      if (!requestedProduct || !card.isConnected || productHandle(card) !== handle) return;
      normalizeSavingBadge(card, badgeText(requestedProduct, selectedVariantFromCard(card, requestedProduct)));
    });
  }

  function updateFromEvent(event) {
    const detail = event.detail || {};
    const card = detail.el?.matches?.(cardSelector) ? detail.el : detail.el?.closest?.(cardSelector);
    if (card) schedule(() => updateCard(card, detail.product, detail.variant));
  }

  let pending = false;
  function updateAllCards() {
    if (pending) return;
    pending = true;
    schedule(() => {
      pending = false;
      document.querySelectorAll(cardSelector).forEach((card) => updateCard(card));
    });
  }

  function updateAddedCards(records) {
    const cards = new Set();
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches(cardSelector)) cards.add(node);
      node.querySelectorAll?.(cardSelector).forEach((card) => cards.add(card));
      node.closest?.(cardSelector) && cards.add(node.closest(cardSelector));
    }));
    if (cards.size) schedule(() => cards.forEach((card) => updateCard(card)));
  }

  document.addEventListener('sezerium:price:update', updateFromEvent);
  document.addEventListener('sezerium:js:load', () => setTimeout(updateAllCards, 0));
  document.addEventListener('sezerium:page:load', () => setTimeout(updateAllCards, 0));
  document.addEventListener('shopify:section:load', () => setTimeout(updateAllCards, 0));
  document.addEventListener('DOMContentLoaded', () => {
    updateAllCards();
    const collectionRoot = document.querySelector('#MainContent') || document.querySelector('main') || document.body;
    new MutationObserver(updateAddedCards).observe(collectionRoot, { childList: true, subtree: true });
  });
})();
